import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { BrowserAuthErrorCodes } from '@azure/msal-browser';
import { getB2cRedirectError } from './b2c-redirect-error';
import { rememberReturnUrl } from './return-url';

/**
 * What every route guard does when MSAL has no signed-in account: send the user to the Azure AD
 * B2C sign-in page and, once they are back, onto `returnUrl` — the page they asked for — rather
 * than the role's home (WI 3861: a bookmark or a reload of a deep route used to come back on
 * `/extranet/admin`). Always cancels the current navigation (returns false); the redirect takes
 * the page over.
 *
 * - A pending B2C redirect error (e.g. AADB2C90118, "Forgot your password?") goes to /login
 *   instead, which shows it — calling loginRedirect() here would silently restart the same policy
 *   with no explanation (see b2c-redirect-error.ts / LoginComponent).
 * - A sign-in that cannot even start (network, MSAL error) also goes to /login, which retries and,
 *   failing again, says so with a retry button — it used to be an unhandled error that left the
 *   page blank. `interaction_in_progress` is the exception: another redirect is already taking
 *   the page away, there is nothing to show.
 */
export function startSignIn(msal: MsalService, router: Router, returnUrl: string): false {
  if (getB2cRedirectError()) {
    router.navigate(['/login']);
    return false;
  }
  rememberReturnUrl(returnUrl);
  msal.loginRedirect().subscribe({
    error: (err: unknown) => {
      if ((err as { errorCode?: string } | null)?.errorCode === BrowserAuthErrorCodes.interactionInProgress) return;
      console.error('[Auth] sign-in redirect failed to start:', err);
      router.navigate(['/login']);
    },
  });
  return false;
}
